import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { login as apiLogin, logout as apiLogout, getCurrentUser } from '../api/client';

// User roles
export const UserRole = {
  DASHBOARD: 'dashboard',
  DEMOS_ADMIN: 'demos_admin',
  SUPER_ADMIN: 'super_admin',
};

// Role hierarchy - higher roles have access to lower role capabilities
const roleHierarchy = {
  [UserRole.SUPER_ADMIN]: [UserRole.SUPER_ADMIN, UserRole.DEMOS_ADMIN, UserRole.DASHBOARD],
  [UserRole.DEMOS_ADMIN]: [UserRole.DEMOS_ADMIN, UserRole.DASHBOARD],
  [UserRole.DASHBOARD]: [UserRole.DASHBOARD],
};

// Access control configuration
const routeAccess = {
  '/dashboard': [UserRole.DASHBOARD, UserRole.DEMOS_ADMIN, UserRole.SUPER_ADMIN],
  '/admin': [UserRole.SUPER_ADMIN],
  '/admin/touchstream': [UserRole.DEMOS_ADMIN, UserRole.SUPER_ADMIN],
  '/admin/s3': [UserRole.SUPER_ADMIN],
  '/admin/e2e-scenarios': [UserRole.DEMOS_ADMIN, UserRole.SUPER_ADMIN],
  '/admin/users': [UserRole.SUPER_ADMIN],
};

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Initialize auth state from localStorage
  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (token && storedUser) {
        try {
          // Verify token is still valid by fetching current user
          const userData = await getCurrentUser();
          setUser(userData);
          localStorage.setItem('user', JSON.stringify(userData));
        } catch (err) {
          // Token is invalid or expired
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          setUser(null);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (username, password) => {
    setLoading(true);
    setError(null);

    try {
      const response = await apiLogin(username, password);
      const { access_token, user: userData } = response;

      localStorage.setItem('token', access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);

      return userData;
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Login failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch (err) {
      // Ignore errors on logout
    } finally {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  }, []);

  // Check if user has a specific role (considering role hierarchy)
  const hasRole = useCallback((role) => {
    if (!user) return false;
    const userRoles = roleHierarchy[user.role] || [];
    return userRoles.includes(role);
  }, [user]);

  // Check if user can access a specific route
  const canAccess = useCallback((path) => {
    if (!user) return false;

    // Find the most specific matching route
    const matchingRoutes = Object.keys(routeAccess)
      .filter((route) => path.startsWith(route))
      .sort((a, b) => b.length - a.length);

    if (matchingRoutes.length === 0) {
      // No specific rule, allow access for any authenticated user
      return true;
    }

    const allowedRoles = routeAccess[matchingRoutes[0]];
    return allowedRoles.includes(user.role);
  }, [user]);

  // Check if user can edit scenario templates
  const canEditTemplates = useCallback(() => {
    return hasRole(UserRole.SUPER_ADMIN);
  }, [hasRole]);

  // Check if user can view scenario templates
  const canViewTemplates = useCallback(() => {
    return hasRole(UserRole.DEMOS_ADMIN) || hasRole(UserRole.SUPER_ADMIN);
  }, [hasRole]);

  // Check if user can manage pipeline scenarios
  const canManagePipelineScenarios = useCallback(() => {
    return hasRole(UserRole.DEMOS_ADMIN) || hasRole(UserRole.SUPER_ADMIN);
  }, [hasRole]);

  // Check if user can manage pipelines
  const canManagePipelines = useCallback(() => {
    return hasRole(UserRole.SUPER_ADMIN);
  }, [hasRole]);

  // Check if user can manage users
  const canManageUsers = useCallback(() => {
    return hasRole(UserRole.SUPER_ADMIN);
  }, [hasRole]);

  // Convenience getters
  const isAuthenticated = !!user;
  const isSuperAdmin = hasRole(UserRole.SUPER_ADMIN);
  const isDemosAdmin = hasRole(UserRole.DEMOS_ADMIN);
  const isDashboardUser = hasRole(UserRole.DASHBOARD);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    hasRole,
    canAccess,
    canEditTemplates,
    canViewTemplates,
    canManagePipelineScenarios,
    canManagePipelines,
    canManageUsers,
    isAuthenticated,
    isSuperAdmin,
    isDemosAdmin,
    isDashboardUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
